'use client'

import { isNull } from 'lodash-es'
import { useCallback, useRef } from 'react'

import {
  getOverlayId,
  type BaseOverlay,
  type BaseOverlayOptions,
} from './overlay'
import { type TCoordinates } from '../../map-manager'
import { mapManager, type Map } from '../../map-manager'

type OverlayClass<
  TOverlay extends BaseOverlay,
  TOverlayOptions extends Omit<BaseOverlayOptions, 'map' | 'position'>,
> = new (options: BaseOverlayOptions & TOverlayOptions) => TOverlay

type Props<
  TOverlay extends BaseOverlay,
  TOverlayOptions extends Omit<BaseOverlayOptions, 'map' | 'position'>,
  TMarker,
> = {
  createInstance: () => OverlayClass<TOverlay, TOverlayOptions>
  getOverlayOptions: (item: TMarker) => TOverlayOptions
}

export const useOverlay = <
  TOverlay extends BaseOverlay,
  TOverlayOptions extends Omit<BaseOverlayOptions, 'map' | 'position'>,
  TMarker extends TCoordinates,
>({
  createInstance,
  getOverlayOptions,
}: Props<TOverlay, TOverlayOptions, TMarker>) => {
  const overlayRef = useRef<TOverlay[]>([])
  const Overlay = useRef<OverlayClass<TOverlay, TOverlayOptions> | null>(null)

  const getOverlayInstance = useCallback(() => {
    if (!Overlay.current) {
      Overlay.current = createInstance()
    }

    return Overlay.current
  }, [createInstance])

  const create = useCallback(
    (map: Map, item: TMarker) => {
      const OverlayClass = getOverlayInstance()

      const position = { lat: item.lat, lng: item.lng }
      const overlay = new OverlayClass({
        ...getOverlayOptions(item),
        map,
        position,
      })

      overlayRef.current.push(overlay)

      return overlay
    },
    [getOverlayInstance, getOverlayOptions],
  )

  const filterVisibleOverlay = useCallback(() => {
    const map = mapManager.getMap()

    if (isNull(map)) return []

    const bounds = map.getBounds()

    return overlayRef.current.filter(overlay => {
      const position = overlay.getPosition()
      const isVisible = bounds.hasPoint(position)

      if (!isVisible) {
        overlay.setMap(null)

        return false
      }

      return true
    })
  }, [])

  const clear = useCallback(() => {
    overlayRef.current.forEach(overlay => {
      overlay.setMap(null)
    })

    overlayRef.current = []
  }, [])

  const update = useCallback(
    (data: TMarker[], { reset }: { reset?: boolean } = {}) => {
      mapManager.withMap(map => {
        if (data.length === 0) {
          clear()

          return
        }

        if (overlayRef.current.length === 0) {
          data.forEach(item => {
            const overlay = create(map, item)
            overlayRef.current.push(overlay)
          })

          return
        }

        if (reset) {
          clear()

          data.forEach(item => {
            const overlay = create(map, item)
            overlayRef.current.push(overlay)
          })

          return
        }

        const filteredOverlays = filterVisibleOverlay()
        overlayRef.current = filteredOverlays

        const existingOverlayIds = new Set(
          filteredOverlays.map(overlay => overlay.id),
        )

        const shouldCreateOverlay = (lat: number, lng: number) => {
          const latLng = new naver.maps.LatLng(lat, lng)
          const overlayId = getOverlayId(latLng)

          return (
            map.getBounds().hasPoint(latLng) &&
            !existingOverlayIds.has(overlayId)
          )
        }

        data.forEach(item => {
          if (shouldCreateOverlay(item.lat, item.lng)) {
            const overlay = create(map, item)
            overlayRef.current.push(overlay)
          }
        })
      })
    },
    [clear, create, filterVisibleOverlay],
  )

  return {
    ref: overlayRef,
    update,
    clear,
  }
}
